"use client"

import { useEffect } from "react"
import * as Sentry from "@sentry/nextjs"
import { useLocale } from "next-intl"
import { useCountry } from "@/components/layout/country-provider"
import { useBookingFlow } from "@/components/layout/booking-flow-provider"
import { buildSiteTools, type SiteTool } from "@/lib/webmcp/site-tools"
import type { AgentCatalog } from "@/lib/webmcp/catalog"

/**
 * The browser's WebMCP entry point. Chrome's current docs put it on
 * `document`; earlier drafts and the explainer used `navigator`. Read both
 * until the origin trial settles, here and nowhere else.
 */
interface ModelContext {
  registerTool: (tool: SiteTool, options: { signal: AbortSignal }) => unknown
}

export function modelContext(): ModelContext | undefined {
  const fromDocument = (document as Document & { modelContext?: ModelContext })
    .modelContext
  const fromNavigator = (
    navigator as Navigator & { modelContext?: ModelContext }
  ).modelContext
  return fromDocument ?? fromNavigator
}

/** A mirror or llms.txt, never an HTML page (a not-found shell, a route the
 *  mirror does not cover) passed off as markdown. */
async function fetchMarkdown(path: string): Promise<string> {
  const response = await fetch(path)
  if (!response.ok) {
    throw new Error(`${path} answered ${response.status}`)
  }
  const type = response.headers.get("content-type")
  if (!type || !/^text\/(markdown|plain)\b/.test(type)) {
    throw new Error(`${path} answered ${type}, not markdown`)
  }
  return response.text()
}

const fetched = new Map<string, Promise<string>>()

/**
 * `fetchMarkdown`, once per path per tab. The mirrors are rendered per
 * request (no edge cache), and an agent may call the same tool in a loop:
 * this caps it at one request per page for the life of the tab. A failure is
 * not kept, so a retry fetches again.
 */
export function fetchText(path: string): Promise<string> {
  const known = fetched.get(path)
  if (known) return known
  const request = fetchMarkdown(path)
  fetched.set(path, request)
  request.catch(() => fetched.delete(path))
  return request
}

const catalogs = new Map<string, Promise<AgentCatalog>>()

/** The market's catalog (app/api/webmcp/catalog), once per tab. A failure is
 *  not kept, so the next tool call asks again. */
export function fetchCatalog(
  country: string,
  locale: string
): Promise<AgentCatalog> {
  const key = `${country}/${locale}`
  const known = catalogs.get(key)
  if (known) return known
  const request = fetch(`/api/webmcp/catalog/${country}/${locale}`).then(
    async (response) => {
      if (!response.ok) {
        throw new Error(`catalog ${key} answered ${response.status}`)
      }
      return (await response.json()) as AgentCatalog
    }
  )
  catalogs.set(key, request)
  request.catch(() => catalogs.delete(key))
  return request
}

/**
 * Registers the site's WebMCP tools (lib/webmcp/site-tools.ts) for the
 * market being browsed, and withdraws them when the market changes or the
 * shell unmounts. Renders nothing; does nothing in a browser without
 * WebMCP. Mounted from the locale layout behind `WEBMCP_ENABLED`.
 */
export function SiteToolsRegistrar() {
  const { code: country } = useCountry()
  const locale = useLocale()
  const flow = useBookingFlow()

  useEffect(() => {
    const context = modelContext()
    if (!context) return
    const controller = new AbortController()
    // Through a promise, so an API change in the origin trial that makes
    // registerTool throw goes to Sentry instead of unmounting the page
    // through the nearest error boundary.
    const register = (tool: SiteTool) =>
      Promise.resolve()
        .then(() => {
          if (controller.signal.aborted) return
          return context.registerTool(tool, { signal: controller.signal })
        })
        .catch((error: unknown) =>
          Sentry.captureException(error, { tags: { webmcpTool: tool.name } })
        )
    buildSiteTools({ country, locale, flow, fetchText }).forEach(register)
    // The commerce tools carry the quiz, coverage and search engines: load
    // them only in a browser that has WebMCP, never in every page's bundle.
    import("@/lib/webmcp/commerce-tools")
      .then(({ buildCommerceTools }) =>
        buildCommerceTools({
          country,
          locale,
          flow,
          catalog: () => fetchCatalog(country, locale),
        }).forEach(register)
      )
      .catch((error: unknown) =>
        Sentry.captureException(error, { tags: { webmcpTool: "commerce" } })
      )
    return () => controller.abort()
  }, [country, locale, flow])

  return null
}
